// CreateWorkOrder.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getERPPartes,
  createOrders,
  getOrderSchemas,
  getERPData,
  getOrdersMeters,
  getPrimeSagemLicenses,
  getERPValidation,
} from "../common/api/orders";
import { getUserByUsername, getUsersByClient } from "../common/api/users";
import { getGroups, getGroupByName } from "../common/api/groups";
import { isAuthenticated, getCurrentUsername } from "../common/api/auth";
import Layout from "../components/Layout";
import OrderCustomSelect from "../components/order/OrderCustomSelect";
import ModalSelectClient from "../components/user-management/ModalSelectClient";
//import JsonSchemaForm from "../components/common/JsonSchemaForm";
import JsonSchemaForm from "../components/common/JsonSchemaFormSelector";
import { useToast } from "../hooks/useToast";

const EMPTY_VALUE = "";
const LATITUDE_RANGE = [-90, 90];
const LONGITUDE_RANGE = [-180, 180];
const INITIAL_COLUMN_FILTERS = {
  cups: "",
  cntId: "",
  nombre: "",
  direccion: "",
  tipoInst: "",
  Te: "",
  coordX: "",
  coordY: "",
  ns: "",
};
const CLIENT_CODE_STORAGE_KEY = "workorders_selected_client_code";
const CLIENT_NAME_STORAGE_KEY = "workorders_selected_client_name";

const FILTERABLE_TYPES = new Set(["2", "3", "4"]);
const TYPE_TO_DATOS_FALTANTES = {
  2: "s02",
  3: "s04",
  4: "s05",
};

const PRIME_LICENSE_KEY_ALIASES = {
  codigo: [
    "CODIGO",
    "Codigo",
    "codigo",
    "CODIGODELICENCIA", // ✅ Aggiungi
    "CODIGO_DE_LICENCIA", // ✅ Aggiungi
    "CodigoLicencia", // ✅ Aggiungi
    "codigo_licencia", // ✅ Aggiungi
  ],
  activo: [
    "ACTIVO",
    "Activo",
    "activo",
    "PRIME14ACTIVO", // ✅ Aggiungi
    "PRIME_1_4_ACTIVO", // ✅ Aggiungi
    "Prime14Activo", // ✅ Aggiungi
    "prime_1_4_activo", // ✅ Aggiungi
    "PRIME14ACTIVO",
    "Prime1_4Activo",
  ],
  canal: [
    "CANAL",
    "Canal",
    "canal",
  ],
  cntid: ["CNTID", "CntId", "cntId", "cntid"],
  cups: ["CUPS", "Cups", "cups"],
};

const TYPE6_PRIME_FIELDS = ["CODIGO", "ACTIVO", "CANAL"];

const TYPE1_FORM_KEY_ALIASES = {
  CUPS: ["CUPS", "CODIGO_CUPS", "CODCUPS"],
  CNTID: ["CNTID", "NS", "NUMEROSERIE", "SERIE", "SERIAL", "SERIENUMERO"],
  NOMBRECT: ["NOMBRECT", "NOMBRE_CT", "CENTROTRABAJO"],
  DIRECCIONSUMINISTRO: [
    "DIRECCIONSUMINISTRO",
    "DIRECCION",
    "DIRECCION_SUMINISTRO",
    "DIRECCIONSUMINISTRO",
  ],
  RUTA: ["RUTA", "CODTRAFO", "CODIGO_TRAFO"],
  TE: ["TE", "TIPOEQUIPO"],
  TIPOINSTALACION: [
    "TIPO_INSTALACION",
    "TIPO_INSTALACION1",
    "TIPOINSTALACION",
    "TIPOINSTALACION1",
  ],
  TECNOLOGIA: ["TECNOLOGIA", "TECHNOLOGIA"],
  SITUACION: ["SITUACION", "ESTADO"],
  PROCESOFACTURACION: ["PROCESOFACTURACION"],
  MAXIMETRO: ["MAXIMETRO"],
  TARIFA: ["TARIFA", "TARIFA_ACTUAL"],
  ERPP1: ["ERP_P1", "PCP1"],
  ERPP2: ["ERP_P2", "PCP2"],
  ERPP3: ["ERP_P3", "PCP3"],
  ERPP4: ["ERP_P4", "PCP4"],
  ERPP5: ["ERP_P5", "PCP5"],
  ERPP6: ["ERP_P6", "PCP6"],
  EXPORTACION: ["EXPORTACION"],
  EXPPOW: ["EXPPOW", "POTENCIARECONOCIDA", "POTENCIA_RECONOCIDA"],
  POTENCIARECONOCIDA: [
    "POTENCIARECONOCIDA",
    "POTENCIA_RECONOCIDA",
    "POTENCIACONTRATADA",
    "POTENCIA_CONTRATADA",
  ],
  FAB: ["FAB", "FABRICANTE"],
  MODEL: ["MODEL", "MODELO"],
  VR: ["VR", "VOLTAJE", "TENSION", "VOLTAGE"],
  AF: ["AF", "ANOFAB", "ANOFABRICACION", "ANOFABRICADO"],
  REGION: ["REGION"],
  COORDENADAX: ["COORDENADA_X", "LATITUD", "LATITUD_WGS84", "LATITUDWGS84"],
  COORDENADAY: ["COORDENADA_Y", "LONGITUD", "LONGITUD_WGS84", "LONGITUDWGS84"],
  TIPOPM: ["TIPO_PM"],
  ID5: ["ID5"],
};

const TYPE_234_DATE_FIELDS = [
  "Fecha_Desde",
  "Fecha_Hasta",
  "Concentrator_identifier",
];

const TYPE_234_FIELD_ALIASES = {
  Fecha_Desde: [
    "Fecha_Desde",
    "FECHA_DESDE",
    "FechaDesde",
    "fecha_desde",
    "FECHAINICIO",
    "FechaInicio",
  ],
  Fecha_Hasta: [
    "Fecha_Hasta",
    "FECHA_HASTA",
    "FechaHasta",
    "fecha_hasta",
    "FECHAFIN",
    "FechaFin",
  ],
  Concentrator_identifier: [
    "Concentrator_identifier",
    "CONCENTRATOR_IDENTIFIER",
    "ConcentratorIdentifier",
    "concentrator_identifier",
    "CONCENTRADOR",
    "Concentrador",
  ],
};

const padNumber = (value) => String(value).padStart(2, "0");

const getTodayISODate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${padNumber(now.getMonth() + 1)}-${padNumber(
    now.getDate()
  )}T23:59:59`;
};

const getFirstDayOfMonthISO = (baseDate = new Date()) => {
  return `${baseDate.getFullYear()}-${padNumber(
    baseDate.getMonth() + 1
  )}-01T00:00:00`;
};

const getLastDayOfMonthISO = (baseDate = new Date()) => {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth() + 1;
  // ottieni l’ultimo giorno del mese corrente
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${padNumber(month)}-${padNumber(lastDay)}T23:59:59`;
};

const getCurrentYearMonthValue = (baseDate = new Date()) => {
  return `${baseDate.getFullYear()}-${padNumber(baseDate.getMonth() + 1)}`;
};

const monthValueToISODate = (monthValue) => {
  if (!monthValue) return "";
  return `${monthValue}-01`;
};

const formatShortValue = (value) => {
  if (!value && value !== 0) {
    return "";
  }

  const stringValue = String(value);

  if (stringValue.length <= 5) {
    return stringValue;
  }

  return `${stringValue.slice(0, 5)}...`;
};


function normalizeSchemasResponse(rawSchemas) {
  if (!rawSchemas) return [];

  // Se è un singolo JSON Schema con allOf (come quello dell'API)
  if (
    rawSchemas.$schema &&
    rawSchemas.properties &&
    Array.isArray(rawSchemas.allOf)
  ) {
    const tipos = rawSchemas.allOf
      .map((block) => {
        const tipoValue = block.if?.properties?.TipoParte?.const;
        const descripcion = block.then?.properties?.TipoParteDescripcion?.const;
        if (tipoValue !== undefined) {
          // ✅ CORREZIONE: Estrai paramsOrder correttamente
          let paramsOrder;

          // Se i parametri sono dentro Params.properties, usa quelli
          if (block.then?.properties?.Params?.properties) {
            paramsOrder = Object.keys(block.then.properties.Params.properties);
          } else if (rawSchemas.properties.Params?.properties) {
            paramsOrder = Object.keys(rawSchemas.properties.Params.properties);
          } else {
            // Altrimenti usa le chiavi del root
            paramsOrder = Object.keys(rawSchemas.properties || {});
          }

          return {
            id: String(tipoValue),
            label: descripcion || `Tipo ${tipoValue}`,
            schema: rawSchemas, // lo schema principale
            paramsOrder: paramsOrder,
          };
        }
        return null;
      })
      .filter(Boolean);
    return tipos;
  }

  // Fallback per vecchi formati
  const schemaItems = Array.isArray(rawSchemas)
    ? rawSchemas
    : rawSchemas.schemas || rawSchemas.Schemas || rawSchemas.data || [];
  return schemaItems.map((item) => ({
    id: String(item.id || item.TipoParte || ""),
    label: item.label || item.description || `Tipo ${item.id}`,
    schema: item.schema || item,
    paramsOrder: Object.keys(item.schema?.properties || {}),
  }));
}

function getFirstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
}

function buildPrimeLicenseKeyCandidates(key) {
  const rawKey = String(key ?? "").trim();

  if (!rawKey) {
    return [];
  }

  // ✅ AGGIUNTO: Normalizza gli accenti come in normalizeFieldKey
  const normalized = rawKey
    .normalize("NFD")  // Decompone i caratteri accentati
    .replace(/[\u0300-\u036f]/g, "")  // Rimuove i diacritici
    .replace(/[\s-]+/g, "_");

  const withoutPrimePrefix = normalized.replace(/^prime_?/i, "");
  const collapsed = withoutPrimePrefix.replace(/_/g, "");
  const aliasKey = collapsed.toLowerCase();

  const candidates = new Set([
    rawKey,
    normalized,
    withoutPrimePrefix,
    collapsed,
  ]);

  const addVariantCases = (variant) => {
    if (!variant) return;
    candidates.add(variant.toUpperCase());
    candidates.add(variant.toLowerCase());
    const camelCaseVariant = variant.charAt(0).toLowerCase() + variant.slice(1);
    candidates.add(camelCaseVariant);
  };

  for (const variant of [...candidates]) {
    addVariantCases(variant);
  }

  const aliasList = PRIME_LICENSE_KEY_ALIASES[aliasKey];
  if (aliasList) {
    aliasList.forEach((alias) => candidates.add(alias));
  }

  return Array.from(candidates).filter(Boolean);
}

function getPrimeValueFromSource(source, key) {
  if (!source || !key) {
    return undefined;
  }

  const candidates = buildPrimeLicenseKeyCandidates(key);

  for (const candidate of candidates) {
    if (candidate in source) {
      return source[candidate];
    }
  }

  return undefined;
}

function resolveValueFromMeter(meter, key) {
  if (!meter) return undefined;

  const { partes = {}, erp = {}, base = {}, primeLicense = {} } = meter;
  const primeValue = getPrimeValueFromSource(primeLicense, key);

  return getFirstDefined(partes[key], primeValue, erp[key], base[key]);
}

function isEmptyValue(value) {
  return value === undefined || value === null || value === "";
}

function extractCodId(record) {
  if (!record || typeof record !== "object") {
    return undefined;
  }

  return getFirstDefined(
    record.COD_ID,
    record.cod_id,
    record.CodId,
    record.codId
  );
}

function normalizeFieldKey(key) {
  if (key === undefined || key === null) {
    return "";
  }

  const result = String(key)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

  // Log temporaneo per debugging TipoParte 6
  if (
    key.includes("Código") ||
    key.includes("Prime") ||
    key.includes("Canal")
  ) {
    console.log(`      normalizeFieldKey("${key}") → "${result}"`);
  }

  return result;
}

function buildNormalizedDataIndex(...sources) {
  const index = new Map();

  sources.forEach((source) => {
    if (!source || typeof source !== "object") {
      return;
    }
    //console.log(source);
    Object.entries(source).forEach(([key, value]) => {
      //console.log(`🔍 Processing key: "${key}", value: "${value}"`);
      /*if (isEmptyValue(value)) {
        return;
      }*/

      const normalizedKey = normalizeFieldKey(key);
      //console.log(`  → Normalized key: "${normalizedKey}"`);
      if (!normalizedKey) { // || index.has(normalizedKey)
        //console.log(`❌ BLOCCATO: normalizedKey vuota per "${key}"`);
        return;
      }

      //index.set(normalizedKey, value);
      //console.log(`✅ Setting index["${normalizedKey}"] = "${value}"`);
      index.set(normalizedKey, value ?? "");
    });
  });

  return index;
}

function getValueFromIndex(index, key) {
  if (!index || !key) {
    return undefined;
  }

  const normalizedKey = normalizeFieldKey(key);
  if (!normalizedKey) {
    return undefined;
  }

  return index.get(normalizedKey);
}

function resolveType1FormFieldValue(formKey, index) {
  //console.log(`    🔎 resolveType1FormFieldValue for "${formKey}"`);
  if (!formKey) {
    return undefined;
  }
  const normalizedKey = normalizeFieldKey(formKey);
  //console.log(`    🔎 Normalized key: "${normalizedKey}"`);

  if (!normalizedKey) {
    return undefined;
  }

  const directValue = getValueFromIndex(index, normalizedKey);
  if (!isEmptyValue(directValue)) {
    //console.log(`    ✅ Found direct value:`, directValue);
    return directValue;
  }

  const aliasList = TYPE1_FORM_KEY_ALIASES[normalizedKey];
  //console.log(`    🔎 Aliases for "${normalizedKey}":`, aliasList);

  if (aliasList) {
    for (const alias of aliasList) {
      const aliasValue = getValueFromIndex(index, alias);
      if (!isEmptyValue(aliasValue)) {
        //console.log(`    ✅ Found via alias "${alias}":`, aliasValue);
        return aliasValue;
      }
    }
  }

  const potenciaMatch = normalizedKey.match(/POTENCIA.*([1-6])/);
  if (potenciaMatch) {
    const potenciaCandidate = `PCP${potenciaMatch[1]}`;
    const potenciaValue = getValueFromIndex(index, potenciaCandidate);
    if (!isEmptyValue(potenciaValue)) {
      return potenciaValue;
    }
  }

  if (/PCP([1-6])$/.test(normalizedKey)) {
    const potenciaValue = getValueFromIndex(index, normalizedKey);
    if (!isEmptyValue(potenciaValue)) {
      return potenciaValue;
    }
  }

  if (normalizedKey.includes("COORD") || normalizedKey.includes("GPS")) {
    if (normalizedKey.includes("X") || normalizedKey.includes("LAT")) {
      const coordX = getValueFromIndex(index, "COORDENADA_X");
      if (!isEmptyValue(coordX)) {
        return coordX;
      }
    }

    if (normalizedKey.includes("Y") || normalizedKey.includes("LON")) {
      const coordY = getValueFromIndex(index, "COORDENADA_Y");
      if (!isEmptyValue(coordY)) {
        return coordY;
      }
    }
  }

  if (normalizedKey.includes("SERIE") || normalizedKey.includes("NS")) {
    const serialValue = getValueFromIndex(index, "NS");
    if (!isEmptyValue(serialValue)) {
      return serialValue;
    }

    const cntIdValue = getValueFromIndex(index, "CNTID");
    if (!isEmptyValue(cntIdValue)) {
      return cntIdValue;
    }
  }

  if (normalizedKey.includes("FABRIC")) {
    const fabValue = getValueFromIndex(index, "FAB");
    if (!isEmptyValue(fabValue)) {
      return fabValue;
    }
  }

  if (normalizedKey.includes("MODE")) {
    const modelValue = getValueFromIndex(index, "MODEL");
    if (!isEmptyValue(modelValue)) {
      return modelValue;
    }
  }

  if (
    normalizedKey.includes("ANIO") ||
    normalizedKey.includes("ANO") ||
    normalizedKey.includes("YEAR")
  ) {
    const yearValue = getValueFromIndex(index, "AF");
    if (!isEmptyValue(yearValue)) {
      return yearValue;
    }
  }

  if (
    normalizedKey.includes("VOLTA") ||
    normalizedKey.includes("TENSION") ||
    normalizedKey === "VR"
  ) {
    const vrValue = getValueFromIndex(index, "VR");
    if (!isEmptyValue(vrValue)) {
      return vrValue;
    }
  }

  if (normalizedKey.includes("AUTOCONSUMO")) {
    const autoconsumoValue = getValueFromIndex(index, "AUTOCONSUMO");
    if (!isEmptyValue(autoconsumoValue)) {
      return autoconsumoValue;
    }
  }

  for (const [candidateKey, candidateValue] of index.entries()) {
    if (isEmptyValue(candidateValue)) {
      continue;
    }

    if (
      candidateKey === normalizedKey ||
      candidateKey.includes(normalizedKey) ||
      normalizedKey.includes(candidateKey)
    ) {
      return candidateValue;
    }
  }

  return undefined;
}

function formatParamValue(value, propertySchema) {
  if (value === undefined || value === null) {
    return "";
  }

  if (propertySchema?.type === "boolean") {
    if (typeof value === "string") {
      const normalized = value.toLowerCase();
      return normalized === "true" ||
        normalized === "si" ||
        normalized === "sí" ||
        normalized === "1" ? "SI" : "NO";
    }
    return value ? "SI" : "NO";
  }

  if (propertySchema?.type === "number" || propertySchema?.type === "integer") {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return "";
    }
    return numeric.toString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(",");
  }

  return String(value);
}

function computeDefaultFormData(schema, meter) {
  if (!schema?.properties) {
    return {};
  }

  return Object.keys(schema.properties).reduce((acc, key) => {
    const propertySchema = schema.properties[key];
    const valueFromMeter = resolveValueFromMeter(meter, key);

    if (valueFromMeter !== undefined) {
      acc[key] = valueFromMeter;
    } else if (propertySchema.default !== undefined) {
      acc[key] = propertySchema.default;
    }

    return acc;
  }, {});
}

function shallowEqual(objA = {}, objB = {}) {
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => objA[key] === objB[key]);
}

const isDefined = (value) =>
  value !== undefined && value !== null && value !== "";

const isValidCoordinate = (value, [min, max]) => {
  if (!isDefined(value)) {
    return false;
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue) || !Number.isFinite(numericValue)) {
    return false;
  }

  return numericValue >= min && numericValue <= max;
};

const sanitizeOrderPayload = (order) => {
  const sanitized = { ...order };

  Object.keys(sanitized).forEach((key) => {
    const value = sanitized[key];
    if (typeof value === "string") {
      sanitized[key] = value.trim();
    }
  });

  if (!isDefined(sanitized.COD_ID_USER)) {
    delete sanitized.COD_ID_USER;
  }

  if (!isDefined(sanitized.COD_ID_USER_GROUP)) {
    delete sanitized.COD_ID_USER_GROUP;
  }

  if (!Array.isArray(sanitized.PARAMS)) {
    sanitized.PARAMS = [];
  } else {
    sanitized.PARAMS = sanitized.PARAMS.map((param) =>
      param === undefined || param === null ? "" : String(param)
    );
  }

  return sanitized;
};

const validateOrderAgainstSpec = (order) => {
  const errors = [];

  if (!isDefined(order.COD_ID_GROUP)) {
    errors.push("COD_ID_GROUP es obligatorio");
  }

  const hasUser = isDefined(order.COD_ID_USER);
  const hasGroup = isDefined(order.COD_ID_USER_GROUP);

  if ((hasUser && hasGroup) || (!hasUser && !hasGroup)) {
    errors.push(
      "Debes asignar la orden a un usuario o a un grupo (pero no ambos)"
    );
  }

  if (!isDefined(order.TipoParte)) {
    errors.push("TipoParte es obligatorio");
  }

  if (!isDefined(order.CUPS)) {
    errors.push("CUPS es obligatorio");
  }

  if (!isDefined(order.CntId)) {
    errors.push("CntId es obligatorio");
  } else if (String(order.CntId).length !== 13) {
    errors.push("CntId debe contener 13 caracteres");
  }

  if (!isDefined(order.DIRECCIONSUMINISTRO)) {
    errors.push("La dirección de suministro es obligatoria");
  }

  if (!isValidCoordinate(order.COORDENADA_X, LATITUDE_RANGE)) {
    errors.push("COORDENADA_X debe ser una latitud válida en WGS84");
  }

  if (!isValidCoordinate(order.COORDENADA_Y, LONGITUDE_RANGE)) {
    errors.push("COORDENADA_Y debe ser una longitud válida en WGS84");
  }

  if (!isDefined(order.Te)) {
    errors.push("El campo Te es obligatorio");
  }

  if (!isDefined(order.TIPO_INSTALACION)) {
    errors.push("TIPO_INSTALACION es obligatoria");
  }

  if (!Array.isArray(order.PARAMS) || order.PARAMS.length === 0) {
    errors.push("PARAMS debe contener al menos un valor");
  }

  return errors;
};

function CreateWorkOrder() {
  const [cups, setCups] = useState([]);
  const [filteredCups, setFilteredCups] = useState([]);
  const [selectedCups, setSelectedCups] = useState([]);
  const [selectedMeters, setSelectedMeters] = useState([]);
  const [clientName, setClientName] = useState("");
  const [clientCodId, setClientCodId] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMeters, setLoadingMeters] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterCups, setFilterCups] = useState("");
  const [filterTecnico, setFilterTecnico] = useState("");
  const [schemas, setSchemas] = useState([]);
  const [selectedSchemaId, setSelectedSchemaId] = useState(EMPTY_VALUE);
  const [formData, setFormData] = useState({});
  const [assignmentMode, setAssignmentMode] = useState("user");
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(EMPTY_VALUE);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState(EMPTY_VALUE);
  const navigate = useNavigate();
  const toast = useToast();
  const previousBaseCupsRef = useRef(null);
  const previousSchemaIdRef = useRef(null);
  const [monthValue, setMonthValue] = useState(getCurrentYearMonthValue());
  const selectedSchema = useMemo(
    () =>
      schemas.find((schemaItem) => schemaItem.id === selectedSchemaId) || null,
    [schemas, selectedSchemaId]
  );

  const selectedTipoParteId = selectedSchemaId
    ? String(selectedSchemaId)
    : null;
  const isFilterableType = selectedTipoParteId
    ? FILTERABLE_TYPES.has(selectedTipoParteId)
    : false;
  // const isSingleSelectionTipoParte = selectedTipoParteId === "1" || selectedTipoParteId === "6";

  const monthDateISO = useMemo(
    () => monthValueToISODate(monthValue),
    [monthValue]
  );

  const filteredUsers = useMemo(() => {
    if (!userFilter) return users;
    const normalized = userFilter.toLowerCase();
    return users.filter((user) => {
      const text = `${user.USER || ""} ${user.NOMBRE || ""} ${user.COD_ID || ""
        }`.toLowerCase();
      return text.includes(normalized);
    });
  }, [users, userFilter]);

  // Stato per filtri e ordinamento (simile a WorkOrder)
  const [columnFilters, setColumnFilters] = useState({
    ...INITIAL_COLUMN_FILTERS,
  });
  const [showClientModal, setShowClientModal] = useState(false);
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [visibleCount, setVisibleCount] = useState(40); // batch iniziale
  const INITIAL_BATCH_SIZE = 40;
  const BATCH_INCREMENT = 40;
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const [loadingMetersList, setLoadingMetersList] = useState(false);
  const [meterFilterMode, setMeterFilterMode] = useState("all");
  const isCreateDisabled =
    saving ||
    loadingMeters ||
    loadingMetersList ||
    selectedCups.length === 0 ||
    selectedMeters.length !== selectedCups.length;

  // --- Column resizing state ---
  const [columnWidths, setColumnWidths] = useState({
    checkBox: 25,
    cups: 200,
    cntId: 180,
    nombre: 220,
    direccion: 260,
    tipoE: 140,
    tipoInst: 140,
    coordX: 140,
    coordY: 140,
    ns: 140,
    acciones: 100,
  });
  const columnResizeState = useRef({
    columnKey: null,
    startX: 0,
    startWidth: 0,
  });

  const columnStyleMap = useMemo(() => {
    const styles = {};
    Object.entries(columnWidths).forEach(([key, value]) => {
      styles[key] = { width: value, minWidth: value };
    });
    return styles;
  }, [columnWidths]);

  const handleColumnResizeMouseMove = useCallback((event) => {
    const { columnKey, startX, startWidth } = columnResizeState.current;
    if (!columnKey) return;

    const deltaX = event.clientX - startX;
    const newWidth = Math.max(80, startWidth + deltaX);

    setColumnWidths((prevWidths) => {
      if (prevWidths[columnKey] === newWidth) return prevWidths;
      return { ...prevWidths, [columnKey]: newWidth };
    });
  }, []);

  const handleColumnResizeMouseUp = useCallback(() => {
    if (!columnResizeState.current.columnKey) return;

    columnResizeState.current = { columnKey: null, startX: 0, startWidth: 0 };
    document.body.classList.remove("column-resizing");
    document.removeEventListener("mousemove", handleColumnResizeMouseMove);
    document.removeEventListener("mouseup", handleColumnResizeMouseUp);
  }, [handleColumnResizeMouseMove]);

  useEffect(() => {
    return () => {
      document.body.classList.remove("column-resizing");
      document.removeEventListener("mousemove", handleColumnResizeMouseMove);
      document.removeEventListener("mouseup", handleColumnResizeMouseUp);
    };
  }, [handleColumnResizeMouseMove, handleColumnResizeMouseUp]);

  const handleColumnResizeMouseDown = (event, columnKey) => {
    event.preventDefault();
    event.stopPropagation();

    const headerCell = event.currentTarget.parentElement;
    const startWidth = headerCell?.offsetWidth || columnWidths[columnKey] || 0;

    columnResizeState.current = {
      columnKey,
      startX: event.clientX,
      startWidth,
    };

    document.body.classList.add("column-resizing");
    document.addEventListener("mousemove", handleColumnResizeMouseMove);
    document.addEventListener("mouseup", handleColumnResizeMouseUp);
  };

  const handleResizeHandleClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const loadUsersAndGroups = useCallback(async (codCliente) => {
    if (!codCliente) {
      setUsers([]);
      setSelectedUserId(EMPTY_VALUE);
      setGroups([]);
      setSelectedGroupId(EMPTY_VALUE);
      return;
    }

    const [usersResponse, groupsResponse] = await Promise.all([
      getUsersByClient(codCliente).catch((error) => {
        console.warn("No se pudieron cargar usuarios del cliente", error);
        return [];
      }),
      getGroups().catch((error) => {
        console.warn("No se pudieron cargar grupos", error);
        return [];
      }),
    ]);

    const normalizedUsers = Array.isArray(usersResponse) ? usersResponse : [];
    setUsers(normalizedUsers);

    if (normalizedUsers.length > 0) {
      const firstUser = normalizedUsers[0];
      const userId =
        firstUser?.COD_ID ??
        firstUser?.cod_id ??
        firstUser?.id ??
        firstUser?.USER ??
        EMPTY_VALUE;
      setSelectedUserId(userId ? String(userId) : EMPTY_VALUE);
    } else {
      setSelectedUserId(EMPTY_VALUE);
    }

    const groupsRaw = Array.isArray(groupsResponse) ? groupsResponse : [];
    const groupDetails = await Promise.all(
      groupsRaw.map(async (groupItem) => {
        if (!groupItem) return null;

        if (
          groupItem.COD_ID &&
          (groupItem.COD_CLIENTE || groupItem.COD_ID_GROUP)
        ) {
          return groupItem;
        }

        const groupName =
          groupItem.NOMBRE || groupItem.GRUPO || groupItem.name || groupItem;

        if (!groupName) return null;

        try {
          return await getGroupByName(groupName);
        } catch (error) {
          console.warn(
            `No se pudo obtener detalle del grupo ${groupName}`,
            error
          );
          return null;
        }
      })
    );

    const filteredGroupDetails = groupDetails
      .filter(Boolean)
      .filter(
        (group) =>
          String(group.COD_CLIENTE || group.COD_ID_GROUP || "") ===
          String(codCliente)
      );

    setGroups(filteredGroupDetails);

    if (filteredGroupDetails.length > 0) {
      const firstGroupId = filteredGroupDetails[0]?.COD_ID;
      setSelectedGroupId(firstGroupId ? String(firstGroupId) : EMPTY_VALUE);
    } else {
      setSelectedGroupId(EMPTY_VALUE);
    }
  }, []);

  const filteredGroups = useMemo(() => {
    if (!groupFilter) return groups;
    const normalized = groupFilter.toLowerCase();
    return groups.filter((group) => {
      const text = `${group.NOMBRE || ""} ${group.COD_ID || ""}`.toLowerCase();
      return text.includes(normalized);
    });
  }, [groups, groupFilter]);

  function getType234ValueFromSource(source, key) {
    if (!source || !key) {
      return undefined;
    }

    // Prova prima il valore diretto
    if (key in source) {
      return source[key];
    }

    // Poi prova gli alias
    const aliasList = TYPE_234_FIELD_ALIASES[key];
    if (aliasList) {
      for (const alias of aliasList) {
        if (alias in source) {
          return source[alias];
        }
      }
    }

    return undefined;
  }

  const getDefaultDatesForTipoParte = (tipoParteId) => {
    const today = getTodayISODate();
    const firstDay = getFirstDayOfMonthISO();
    const lastDay = getLastDayOfMonthISO();
    switch (tipoParteId) {
      case "2":
      case "4":
        return {
          fechaInicio: firstDay,
          fechaFin: today,
        };
      case "3":
        return {
          fechaInicio: firstDay,
          fechaFin: lastDay,
        };
      default:
        return {
          fechaInicio: null,
          fechaFin: null,
        };
    }
  };
  // INIZIALE
  useEffect(() => {
    const loadUserAndClients = async () => {
      if (!isAuthenticated()) {
        navigate("/login");
        return;
      }

      try {
        const username = getCurrentUsername();
        const user = await getUserByUsername(username);
        setCurrentUser(user);

        const clients = Array.isArray(user?.CLIENTES_ASOCIADOS)
          ? user.CLIENTES_ASOCIADOS
          : user?.COD_CLIENTE
            ? [
              {
                COD_CLIENTE: user.COD_CLIENTE,
                NOMBRE_CLIENTE: user.NOMBRE_CLIENTE,
              },
            ]
            : [];

        setAvailableClients(clients);

        if (clients.length > 0) {
          let defaultClient = clients[0];

          if (typeof window !== "undefined") {
            const storedCode = window.localStorage.getItem(
              CLIENT_CODE_STORAGE_KEY
            );

            if (storedCode) {
              const matchedClient = clients.find(
                (clientItem) =>
                  String(clientItem?.COD_CLIENTE ?? clientItem?.cod_cliente) ===
                  String(storedCode)
              );

              if (matchedClient) {
                defaultClient = matchedClient;
              }
            }
          }

          const defaultClientName = defaultClient?.NOMBRE_CLIENTE || "";
          const defaultClientCode =
            defaultClient?.COD_CLIENTE ?? defaultClient?.cod_cliente ?? "";

          setClientName(defaultClientName);
          setSelectedClient(defaultClientName);
          setClientCodId(String(defaultClientCode));

          if (
            typeof window !== "undefined" &&
            defaultClientCode !== undefined &&
            defaultClientCode !== null &&
            defaultClientCode !== ""
          ) {
            window.localStorage.setItem(
              CLIENT_CODE_STORAGE_KEY,
              String(defaultClientCode)
            );
            window.localStorage.setItem(
              CLIENT_NAME_STORAGE_KEY,
              defaultClientName
            );
            setColumnFilters({ ...INITIAL_COLUMN_FILTERS });
          }
        }
      } catch (err) {
        console.error("Error al cargar usuario o clientes:", err);
        toast.error("Error al cargar los datos del usuario");
      }
    };

    loadUserAndClients();
  }, [navigate, toast]);
  // Manage DATETIME
  useEffect(() => {
    if (!selectedTipoParteId) {
      setMeterFilterMode("all");
      setColumnFilters({ ...INITIAL_COLUMN_FILTERS });
      setVisibleCount(INITIAL_BATCH_SIZE);
      setSelectedCups([]);
      setSelectedMeters([]);
      return;
    }

    if (FILTERABLE_TYPES.has(selectedTipoParteId)) {
      setMeterFilterMode("missing");
    } else {
      setMeterFilterMode("all");
    }

    setColumnFilters({ ...INITIAL_COLUMN_FILTERS });
    setVisibleCount(INITIAL_BATCH_SIZE);
    setSelectedCups([]);
    setSelectedMeters([]);

    /* if (selectedSchema?.schema?.properties) {
       console.log("📋 Schema properties per Fecha_Desde:", selectedSchema.schema.properties.Fecha_Desde );
       console.log("📋 Schema properties per Fecha_Hasta:", selectedSchema.schema.properties.Fecha_Hasta );
 
       // Se sono dentro Params
       if (selectedSchema.schema.properties.Params?.properties) {
         console.log( "📋 Params.Fecha_Desde:", selectedSchema.schema.properties.Params.properties.Fecha_Desde );
         console.log("📋 Params.Fecha_Hasta:", selectedSchema.schema.properties.Params.properties.Fecha_Hasta );
       }
     }
 */
    // ✅ Popola il form con le date default
    const defaultDates = getDefaultDatesForTipoParte(selectedTipoParteId);
    if (defaultDates.fechaInicio && defaultDates.fechaFin) {
      setFormData((prev) => {
        let paramsSchema = selectedSchema?.schema?.properties?.Params;

        // ✅ Handle $ref case
        if (paramsSchema?.$ref && selectedSchema?.schema?.definitions) {
          const refName = paramsSchema.$ref.replace("#/definitions/", "");
          paramsSchema = selectedSchema.schema.definitions?.[refName];
        }

        const hasParams = !!paramsSchema?.properties?.Fecha_Desde;

        if (hasParams) {
          return {
            ...prev,
            Params: {
              ...(prev.Params || {}),
              Fecha_Desde: defaultDates.fechaInicio,
              Fecha_Hasta: defaultDates.fechaFin,
              Concentrator_identifier: "",
            },
          };
        } else {
          return {
            ...prev,
            Fecha_Desde: defaultDates.fechaInicio,
            Fecha_Hasta: defaultDates.fechaFin,
            Concentrator_identifier: "",
          };
        }
      });
      console.log("📅 Default dates SET in formData (ref-safe):", defaultDates);
    }
  }, [selectedTipoParteId, selectedSchema]);

  // CHANGE CLIENT
  useEffect(() => {
    const reloadDataForClient = async () => {
      if (!clientCodId) return;

      setLoading(true);
      setColumnFilters({ ...INITIAL_COLUMN_FILTERS });
      setVisibleCount(INITIAL_BATCH_SIZE);

      try {
        setCups([]);
        setFilteredCups([]);

        const schemasResponse = await getOrderSchemas();
        const normalizedSchemas = normalizeSchemasResponse(schemasResponse);
        setSchemas(normalizedSchemas);

        if (normalizedSchemas.length > 0) {
          const preferredSchema = normalizedSchemas.find(
            (item) => item.id === selectedSchemaId
          );
          setSelectedSchemaId((preferredSchema || normalizedSchemas[0]).id);
        } else {
          setSelectedSchemaId(EMPTY_VALUE);
        }

        await loadUsersAndGroups(clientCodId);
        toast.success(`Datos cargados para el cliente ${clientName}`);
      } catch (err) {
        console.error("Error al recargar datos:", err);

        // 🔍 Registro más detallado
        if (err.response) {
          console.error("➡️ Código de estado:", err.response.status);
          console.error("➡️ Texto de respuesta:", await err.response.text());
        } else if (err instanceof Response) {
          // Si el error es un Response (caso fetch)
          console.error("➡️ Código de estado:", err.status);
          const errorText = await err.text().catch(() => "(sin cuerpo)");
          console.error("➡️ Cuerpo del error:", errorText);
        } else {
          console.error("➡️ Error genérico:", err.message);
        }

        toast.error("Error al cargar los datos del ERP para el cliente");
      } finally {
        setLoading(false);
      }
    };

    reloadDataForClient();
  }, [clientCodId]); // ✅ ora reagisce al cambio di cliente

  useEffect(() => {
    let filtered = cups;

    if (filterCups) {
      filtered = filtered.filter((c) =>
        c.CUPS?.toLowerCase().includes(filterCups.toLowerCase())
      );
    }

    if (filterTecnico) {
      filtered = filtered.filter((c) =>
        c.NOMBRECT?.toLowerCase().includes(filterTecnico.toLowerCase())
      );
    }

    setFilteredCups(filtered);
  }, [filterCups, filterTecnico, cups]);

  const fetchMetersForClient = useCallback(async () => {
    if (!clientCodId || !selectedTipoParteId) {
      return;
    }

    const useFiltered = isFilterableType && meterFilterMode === "missing";
    const datosFaltantes = useFiltered
      ? TYPE_TO_DATOS_FALTANTES[selectedTipoParteId] || null
      : null;

    let fechaInicio;
    let fechaFin;

    if (useFiltered) {
      // ✅ USA le date dal form (o calcola default se non presenti)
      const datesFromForm = {
        fechaInicio: formData?.Fecha_Desde,
        fechaFin: formData?.Fecha_Hasta,
      };

      const defaultDates = getDefaultDatesForTipoParte(selectedTipoParteId);

      fechaInicio = datesFromForm.fechaInicio || defaultDates.fechaInicio;
      fechaFin = datesFromForm.fechaFin || defaultDates.fechaFin;

      if (!fechaInicio || !fechaFin) {
        console.warn("⚠️ Missing dates for filtered query");
        return;
      }

      console.log(`📅 Fetching meters with dates:`, { fechaInicio, fechaFin });
    }

    setLoadingMetersList(true);

    try {
      const meters = await getOrdersMeters(clientCodId, {
        datosFaltantes,
        fechaInicio,
        fechaFin,
      });

      const normalizedMeters = Array.isArray(meters) ? meters : [];
      setCups(normalizedMeters);
      setFilteredCups(normalizedMeters);
      setVisibleCount(INITIAL_BATCH_SIZE);

      setSelectedCups((prev) =>
        prev.filter((selectedItem) =>
          normalizedMeters.some((meter) => meter.CUPS === selectedItem.CUPS)
        )
      );

      setSelectedMeters((prev) =>
        prev.filter((meter) =>
          normalizedMeters.some((item) => item.CUPS === meter.cups)
        )
      );
    } catch (error) {
      console.error("Error al obtener los contadores disponibles", error);
      setCups([]);
      setFilteredCups([]);
      toast.error("No se pudieron cargar los contadores del cliente");
    } finally {
      setLoadingMetersList(false);
    }
  }, [
    clientCodId,
    selectedTipoParteId,
    isFilterableType,
    meterFilterMode,
    formData?.Fecha_Desde,
    formData?.Fecha_Hasta,
    toast,
  ]);

  useEffect(() => {
    fetchMetersForClient();
  }, [fetchMetersForClient]);

  useEffect(() => {
    if (!clientCodId || selectedCups.length === 0) {
      setSelectedMeters([]);
      setLoadingMeters(false);
      return;
    }

    let isMounted = true;

    const loadMetersData = async () => {
      try {
        setLoadingMeters(true);

        const metersData = await Promise.all(
          selectedCups.map(async (item) => {
            const cupsValue = item.CUPS || item.cups || item.Cups;
            try {
              //const shouldFetchPrime = selectedTipoParteId === "6";
              const partesData = await getERPPartes(clientCodId, cupsValue);
              const erpInfoData = await getERPData(cupsValue, clientCodId);
              let validationData = [];
              try {
                validationData = await getERPValidation(clientCodId, cupsValue);
                //console.log(  `📡 Validation data loaded for ${cupsValue}:`,  validationData );
              } catch (error) {
                console.warn(`⚠️ No se pudo obtener validación ERP para ${cupsValue}`, error);
              }
              //console.log(`📊 Data loaded for ${cupsValue}:`);
              //console.log("  📋 Partes data:", partesData);
              //console.log("  📋 ERP data (array):", erpInfoData);

              let primeRecord = null;

              if (selectedTipoParteId === "6") {
                try {
                  const primeLicenseData = await getPrimeSagemLicenses(
                    clientCodId,
                    cupsValue
                  );

                  if (Array.isArray(primeLicenseData)) {
                    primeRecord = primeLicenseData[0] || null;
                  } else if (
                    primeLicenseData &&
                    typeof primeLicenseData === "object" &&
                    Array.isArray(primeLicenseData.data)
                  ) {
                    primeRecord = primeLicenseData.data[0] || null;
                  } else if (
                    primeLicenseData &&
                    typeof primeLicenseData === "object"
                  ) {
                    primeRecord = primeLicenseData;
                  }

                  if (!primeRecord) {
                    console.warn(
                      `No se pudieron cargar licencias Prime para ${cupsValue}`
                    );
                  }
                } catch (error) {
                  console.warn(
                    `No se pudieron cargar licencias Prime para ${cupsValue}`,
                    error
                  );
                }
              }

              const partesList = Array.isArray(partesData)
                ? partesData
                : partesData
                  ? [partesData]
                  : [];
              const erpList = Array.isArray(erpInfoData)
                ? erpInfoData
                : erpInfoData
                  ? [erpInfoData]
                  : [];

              const partesRecord = partesList[0] || null;

              let erpRecord = erpList[0] || null;

              if (selectedTipoParteId === "1") {
                const targetCodId = extractCodId(partesRecord);

                if (targetCodId !== undefined) {
                  const match = erpList.find((candidate) => {
                    const candidateCodId = extractCodId(candidate);

                    return (
                      candidateCodId !== undefined &&
                      String(candidateCodId).trim() ===
                      String(targetCodId).trim()
                    );
                  });

                  if (match) {
                    erpRecord = match;
                  }
                }
              }

              return {
                cups: cupsValue,
                base: item,
                partes: partesRecord || {},
                erp: erpRecord || {},
                validation: validationData[0] || {},
                primeLicense: primeRecord || null,
              };
            } catch (error) {
              console.error(
                `Error al cargar datos ERP para ${cupsValue}`,
                error
              );
              return {
                cups: cupsValue,
                base: item,
                partes: {},
                erp: {},
                validation: {},
                primeLicense: null,
              };
            }
          })
        );

        if (isMounted) {
          setSelectedMeters(metersData);
          //console.log("📊 Loaded meter data:", metersData); // ✅ Aggiungi questo
        }
      } catch (error) {
        console.error(
          "Error al cargar información ampliada de los CUPS seleccionados",
          error
        );
      } finally {
        if (isMounted) {
          setLoadingMeters(false);
        }
      }
    };

    loadMetersData();

    return () => {
      isMounted = false;
    };
  }, [clientCodId, selectedCups, selectedTipoParteId]);

  // Riga selezionata
  useEffect(() => {
    if (!selectedSchema) return;

    const baseMeter = selectedMeters[0] || null;
    const currentCups = baseMeter?.cups || null;

    const schemaChanged = previousSchemaIdRef.current !== selectedSchema.id;
    const cupsChanged = previousBaseCupsRef.current !== currentCups;

    /*console.log("🔍 Form population check:", {
      schemaChanged,
      cupsChanged,
      currentCups,
      selectedTipoParteId,
      hasMeter: !!baseMeter,
      meterKeys: baseMeter ? Object.keys(baseMeter) : [],
    });*/

    if (schemaChanged || cupsChanged) {
      const next = baseMeter
        ? buildFormDataForSelection(
          selectedSchema.schema,
          baseMeter,
          selectedTipoParteId
        )
        : {};

      //console.log("✅ Populating form with data:", next);
      // ✅ PRESERVA LE DATE ESISTENTI SE GIÀ PRESENTI NEL FORM
      const preservedDates = {};
      if (formData?.Fecha_Desde) {
        preservedDates.Fecha_Desde = formData.Fecha_Desde;
      }
      if (formData?.Fecha_Hasta) {
        preservedDates.Fecha_Hasta = formData.Fecha_Hasta;
      }
      setFormData(
        selectedSchema?.schema?.allOf
          ? {
            ...next,
            ...preservedDates,
            TipoParte: Number(selectedSchemaId),
            TipoParteDescripcion:
              selectedSchema.schema.allOf?.find(
                (b) =>
                  b.if?.properties?.TipoParte?.const ===
                  Number(selectedSchemaId)
              )?.then?.properties?.TipoParteDescripcion?.const ||
              selectedSchema.label ||
              "",
          }
          : { ...next, ...preservedDates }
      );

      previousSchemaIdRef.current = selectedSchema.id;
      previousBaseCupsRef.current = currentCups;
    }
  }, [selectedSchema, selectedMeters, selectedTipoParteId, selectedSchemaId]);

  function getSafeFallbackValue(key, propertySchema = {}) {
    // 🔹 Si el campo tiene un default definido en el schema, úsalo
    if (propertySchema.default !== undefined) {
      return propertySchema.default;
    }

    // 🔹 Si el tipo está definido, podemos generar un placeholder razonable
    switch (propertySchema.type) {
      case "string":
        return ""; // o "(sin valor)" si quieres algo visible
      case "number":
      case "integer":
        return 0;
      case "boolean":
        return "NO"; // valor neutral tipo “false”
      case "array":
        return [];
      case "object":
        return {};
      default:
        return ""; // por seguridad
    }
  }
  // Selection of one part on TipoParte 1 or 6
  useEffect(() => {
    if (selectedTipoParteId !== "6") {
      return;
    }

    const baseMeter = selectedMeters[0];
    if (!baseMeter) {
      return;
    }

    const schemaProperties = selectedSchema?.schema?.properties || {};
    const schemaKeys = Object.keys(schemaProperties);

    const findSchemaKeyForPrimeField = (targetKey) => {
      if (!schemaKeys.length) {
        return targetKey;
      }

      const normalizedCandidates = buildPrimeLicenseKeyCandidates(
        targetKey
      ).map((candidate) => normalizeFieldKey(candidate));

      const matchedKey = schemaKeys.find((schemaKey) => {
        const normalizedSchemaKey = normalizeFieldKey(schemaKey);
        return normalizedCandidates.includes(normalizedSchemaKey);
      });

      return matchedKey || targetKey;
    };

    setFormData((prevData = {}) => {
      const nextData = { ...prevData };
      let changed = false;

      TYPE6_PRIME_FIELDS.forEach((primeFieldKey) => {
        const schemaKey = findSchemaKeyForPrimeField(primeFieldKey);
        if (!schemaKey || !schemaProperties[schemaKey]) {
          return;
        }

        const primeValue = resolveValueFromMeter(baseMeter, primeFieldKey);
        if (isEmptyValue(primeValue)) {
          return;
        }

        if (nextData[schemaKey] !== primeValue) {
          nextData[schemaKey] = primeValue;
          changed = true;
        }
      });

      return changed ? nextData : prevData;
    });
  }, [selectedTipoParteId, selectedMeters, selectedSchema]);
  /*
  // Manage single selection for tipo 1,6
  useEffect(() => {
    if (!isSingleSelectionTipoParte) {
      return;
    }

    setSelectedCups((prevSelected) => {
      if (prevSelected.length <= 1) {
        return prevSelected;
      }

      const lastSelected = prevSelected[prevSelected.length - 1];
      return lastSelected ? [lastSelected] : prevSelected.slice(0, 1);
    });
  }, [isSingleSelectionTipoParte]);

  useEffect(() => {
    if (!isSingleSelectionTipoParte) {
      return;
    }

    setSelectedMeters((prevMeters) => {
      if (prevMeters.length <= 1) {
        return prevMeters;
      }

      const lastMeter = prevMeters[prevMeters.length - 1];
      return lastMeter ? [lastMeter] : prevMeters.slice(0, 1);
    });
  }, [isSingleSelectionTipoParte]);
*/
  const handleAssignmentChange = (newMode) => {
    setAssignmentMode(newMode);

    if (newMode === "user") {
      setSelectedGroupId(EMPTY_VALUE);
    } else {
      setSelectedUserId(EMPTY_VALUE);
    }
  };

  const buildParamsForMeter = (meter) => {
    if (!selectedSchema?.schema?.properties) {
      return [];
    }

    // ✅ Tipo 2, 3, 4: Lecturas (s02, s04, s05)
    if (
      selectedTipoParteId === "2" ||
      selectedTipoParteId === "3" ||
      selectedTipoParteId === "4"
    ) {
      const defaultDates = getDefaultDatesForTipoParte(selectedTipoParteId);

      // Controlla se i campi sono in Params o nel root
      const isInParams =
        selectedSchema.schema.properties.Params?.properties?.Fecha_Desde;

      const fechaDesde = isInParams
        ? formData?.Params?.Fecha_Desde || defaultDates.fechaInicio || ""
        : formData?.Fecha_Desde || defaultDates.fechaInicio || "";

      const fechaHasta = isInParams
        ? formData?.Params?.Fecha_Hasta || defaultDates.fechaFin || ""
        : formData?.Fecha_Hasta || defaultDates.fechaFin || "";

      const concentrator = isInParams
        ? formData?.Params?.Concentrator_identifier || ""
        : formData?.Concentrator_identifier || "";

      console.log(`🔧 buildParamsForMeter tipo ${selectedTipoParteId}:`, {
        fechaDesde,
        fechaHasta,
        concentrator,
        isInParams,
      });

      return [fechaDesde, fechaHasta, concentrator].map((value) =>
        value === undefined || value === null ? "" : String(value)
      );
    }

    // ✅ Tipo 5: Lectura Meter parameters (s06)
    if (selectedTipoParteId === "5") {
      const concentrator = formData?.Params?.Concentrator_identifier ||
        formData?.Concentrator_identifier || "";

      //console.log("🔧 buildParamsForMeter tipo 5:", { concentrator });

      return [String(concentrator)];
    }

    // ✅ Tipo 6: Prime1.4 - Sagemcom
    if (selectedTipoParteId === "6") {
      const license = meter?.primeLicense || {};
      const ensureString = (value) =>
        value === undefined || value === null ? "" : String(value);

      const params = [
        ensureString(license.CODIGO || license.Codigo || license.codigo),
        ensureString(license.ACTIVO || license.Activo || license.activo),
        ensureString(license.CANAL || license.Canal || license.canal),
      ];

      //console.log("🔧 buildParamsForMeter tipo 6:", params);

      return params;
    }


    // ✅ Tipo 1: Cambio de contador
    if (selectedTipoParteId === "1") {
      const paramKeys = selectedSchema.paramsOrder || [];

      //console.log("🔧 buildParamsForMeter tipo 1:", { paramKeys,    meterCups: meter?.cups || "unknown"  });

      if (paramKeys.length === 0) {
        console.error("❌ paramKeys è vuoto!");
        return [];
      }

      // 🔥 FIX: Crea l'indice normalizzato per QUESTO meter specifico
      const base = meter?.base || {};
      const partes = meter?.partes || {};
      const erp = meter?.erp || {};
      const validation = meter?.validation || {};

      // Crea l'indice che mappa i nomi dei campi API ai valori
      const index = buildNormalizedDataIndex(erp, partes, base, validation);

      return paramKeys.map((key) => {
        // 1. USA resolveType1FormFieldValue che sa mappare ERP_P1 -> PCP1
        let value = resolveType1FormFieldValue(key, index);

        // 2. Default vuoto
        if (value === undefined || value === null) {
          value = "";
        }

        const formatted = formatParamValue(value, { type: "string" });

        const source =
          resolveType1FormFieldValue(key, index) !== undefined &&
            resolveType1FormFieldValue(key, index) !== null &&
            resolveType1FormFieldValue(key, index) !== ""
            ? "meter API"
            : "formData";

        console.log(`  ✓ ${key}: "${formatted}" (from ${source})`);

        return formatted;
      });
    }

    // ✅ Fallback generico per altri tipi (non dovrebbe essere necessario)
    console.warn("⚠️ buildParamsForMeter: Tipo non gestito:", selectedTipoParteId);

    const hasNestedParams = !!selectedSchema.schema.properties.Params?.properties;

    const paramKeys = hasNestedParams
      ? Object.keys(selectedSchema.schema.properties.Params.properties)
      : (selectedSchema.paramsOrder || Object.keys(selectedSchema.schema.properties));

    return paramKeys.map((key) => {
      const propertySchema = hasNestedParams
        ? selectedSchema.schema.properties.Params.properties[key]
        : selectedSchema.schema.properties[key];

      let value;

      if (hasNestedParams) {
        value = formData?.Params?.[key];
      } else {
        value = formData?.[key];
      }

      if (value === undefined || value === null || value === "") {
        const base = meter?.base || {};
        const partes = meter?.partes || {};
        const erp = meter?.erp || {};

        value = getFirstDefined(
          partes[key],
          erp[key],
          base[key]
        );
      }

      if (value === undefined || value === null || value === "") {
        value = propertySchema?.default || "";
      }

      return formatParamValue(value, propertySchema);
    });
  };

  const buildOrderPayload = (meter) => {
    const base = meter.base || {};
    const partes = meter.partes || {};
    const erp = meter.erp || {};
    const prime = meter.primeLicense || {};

    const order = {
      COD_ID_GROUP: clientCodId ? String(clientCodId) : EMPTY_VALUE,
      TipoParte: selectedTipoParteId || EMPTY_VALUE,
      CUPS:
        getFirstDefined(
          meter.cups,
          base.CUPS,
          partes.CUPS,
          erp.CUPS,
          getPrimeValueFromSource(prime, "CUPS")
        ) || EMPTY_VALUE,
      CntId: getFirstDefined(
        partes.CntId,
        getPrimeValueFromSource(prime, "CntId"),
        base.CntId,
        base.NS,
        erp.CntId,
        erp.NS,
        EMPTY_VALUE
      ),
      NOMBRECT: getFirstDefined(
        partes.NOMBRECT,
        base.NOMBRECT,
        erp.NOMBRECT,
        EMPTY_VALUE
      ),
      DIRECCIONSUMINISTRO: getFirstDefined(
        partes.DIRECCIONSUMINISTRO,
        base.DIRECCIONSUMINISTRO,
        erp.DIRECCIONSUMINISTRO,
        EMPTY_VALUE
      ),
      RUTA: getFirstDefined(
        partes.CODTRAFO,
        base.CODTRAFO,
        erp.CODTRAFO,
        EMPTY_VALUE
      ),
      Te: getFirstDefined(partes.Te, base.Te, erp.Te, "5"),
      TIPO_INSTALACION: getFirstDefined(
        partes.TIPO_INSTALACION,
        base.TIPO_INSTALACION,
        partes.TIPO_INSTALACION_1,
        erp.TIPO_INSTALACION,
        "Monofasica"
      ),
      COORDENADA_X: getFirstDefined(
        partes.COORDENADA_X,
        base.COORDENADA_X,
        erp.COORDENADA_X,
        "0"
      ),
      COORDENADA_Y: getFirstDefined(
        partes.COORDENADA_Y,
        base.COORDENADA_Y,
        erp.COORDENADA_Y,
        "0"
      ),
      PARAMS: buildParamsForMeter(meter),
    };

    if (assignmentMode === "user") {
      order.COD_ID_USER = selectedUserId;
    } else {
      order.COD_ID_USER_GROUP = selectedGroupId;
    }

    return order;
  };

  const handleSaveOrders = async (ordersData) => {
    try {

      //console.log("📦 Payload che verrebbe inviato:", ordersData);
      //return;
      const result = await createOrders(ordersData);
      if (result.status && parseInt(result.status, 10) > 0) {
        toast.success(`${result.status} parte(s) creados correctamente`);
        setSelectedCups([]);
        setSelectedMeters([]);
        return true;
      }
      const createdCups = Array.isArray(result.actualizadas)
        ? result.actualizadas
        : [];
      const hasPartialCreation = createdCups.length > 0;

      if (hasPartialCreation) {
        toast.warning(`Solo se pudieron crear ${createdCups.length} parte(s). Revisa los datos de los CUPS restantes.`);
      } else {
        toast.error("La API no creó ninguna orden. Verifica permisos, asignación, CUPS, coordenadas y que no existan partes NotStarted con el mismo TipoParte.");
      }
      return false;
    } catch (err) {
      console.error("Error al crear partes:", err);
      toast.error(`Error al crear partes: ${err.message}`);
      return false;
    }
  };
  /*
    const handleCreateOrdersClick = async () => {
      if (selectedCups.length === 0) {
        toast.error("Selecciona al menos un CUPS");
        return;
      }
  
      if (!selectedSchema) {
        toast.error("Selecciona el tipo de parte");
        return;
      }
  
      if (assignmentMode === "user" && !selectedUserId) {
        toast.error("Selecciona un usuario o técnico");
        return;
      }
  
      if (assignmentMode === "group" && !selectedGroupId) {
        toast.error("Selecciona un grupo de usuarios");
        return;
      }
  
      if (selectedMeters.length === 0) {
        if (loadingMeters) {
          toast.error(
            "Espera a que se carguen los datos de los CUPS seleccionados"
          );
        } else {
          toast.error("No hay información de los CUPS seleccionados");
        }
        return;
      }
  
      const validationErrors = validateJsonSchemaFormData(
        selectedSchema.schema,
        formData
      );
      if (validationErrors.length > 0) {
        toast.error(
          `Revisa el formulario: ${validationErrors[0].key} - ${validationErrors[0].message}`
        );
        return;
      }
  
      try {
        setSaving(true);
        const ordersToCreate = selectedMeters
          .map(buildOrderPayload)
          .map(sanitizeOrderPayload);
  
        const invalidOrders = ordersToCreate
          .map((order) => ({
            order,
            errors: validateOrderAgainstSpec(order),
          }))
          .filter(({ errors }) => errors.length > 0);
  
        if (invalidOrders.length > 0) {
          const { order, errors } = invalidOrders[0];
          const cupsLabel = order.CUPS ? ` (${order.CUPS})` : "";
          toast.error(`Revisa la orden${cupsLabel}: ${errors[0]}`);
          console.warn("❌ Orden inválida según especificaciones", invalidOrders);
          return;
        }
        // 🧩 LOG DETTAGLIATO DEL PAYLOAD
        console.groupCollapsed("📦 Payload ordine da inviare (POST /orders)");
        console.log("Numero ordini:", ordersToCreate.length);
        ordersToCreate.forEach((order, i) => {
          console.log(`--- ORDINE #${i + 1} ---`);
          console.table(order);
        });
        console.groupEnd();
        const success = await handleSaveOrders(ordersToCreate);
  
        if (success) {
          setFormData({});
        }
      } finally {
        setSaving(false);
      }
    };*/

  const handleCreateOrdersClick = async () => {
    if (saving) {
      console.log("⚠️ Already saving, ignoring duplicate click");
      return;
    }

    if (!selectedSchema) {
      toast.error("Selecciona el tipo de parte");
      return;
    }

    if (assignmentMode === "user" && !selectedUserId) {
      toast.error("Selecciona un usuario o técnico");
      return;
    }

    if (assignmentMode === "group" && !selectedGroupId) {
      toast.error("Selecciona un grupo de usuarios");
      return;
    }

    if (!selectedCups.length) {
      toast.error("Selecciona al menos un CUPS");
      return;
    }

    if (!selectedMeters.length || selectedMeters.length !== selectedCups.length) {
      toast.error("Esperando la carga de datos de contadores...");
      return;
    }

    if (loadingMeters) {
      toast.error("Esperando la carga de datos de contadores...");
      return;
    }

    try {
      setSaving(true);

      // 🔥 NEW LOGIC: For tipo 1 and 6, reload data from API
      const isSpecialTipo = selectedTipoParteId === "1" || selectedTipoParteId === "6";

      let metersToUse = selectedMeters;

      if (isSpecialTipo) {
        // Reload fresh data from API for each selected CUPS
        metersToUse = await Promise.all(
          selectedCups.map(async (item) => {
            const cupsValue = item.CUPS || item.cups || item.Cups;

            try {
              const partesData = await getERPPartes(clientCodId, cupsValue);
              const erpInfoData = await getERPData(cupsValue, clientCodId);
              let validationData = [];

              try {
                validationData = await getERPValidation(clientCodId, cupsValue);
              } catch (error) {
                console.warn(`⚠️ No validation data for ${cupsValue}`, error);
              }

              let primeRecord = null;

              // For tipo 6, also reload Prime license data
              if (selectedTipoParteId === "6") {
                try {
                  const primeLicenseData = await getPrimeSagemLicenses(
                    clientCodId,
                    cupsValue
                  );

                  if (Array.isArray(primeLicenseData)) {
                    primeRecord = primeLicenseData[0] || null;
                  } else if (
                    primeLicenseData &&
                    typeof primeLicenseData === "object" &&
                    Array.isArray(primeLicenseData.data)
                  ) {
                    primeRecord = primeLicenseData.data[0] || null;
                  } else if (
                    primeLicenseData &&
                    typeof primeLicenseData === "object"
                  ) {
                    primeRecord = primeLicenseData;
                  }
                } catch (error) {
                  console.warn(
                    `No se pudieron cargar licencias Prime para ${cupsValue}`,
                    error
                  );
                }
              }

              // Normalizza i dati in array
              const partesList = Array.isArray(partesData)
                ? partesData
                : partesData
                  ? [partesData]
                  : [];
              const erpList = Array.isArray(erpInfoData)
                ? erpInfoData
                : erpInfoData
                  ? [erpInfoData]
                  : [];

              // 🔥 FIX: Filtra i dati per il CUPS e CntId corretti
              const partesRecord = partesList.find(p => {
                const pCups = p?.CUPS || p?.cups || p?.Cups;
                const itemCups = item?.CUPS || item?.cups || cupsValue;
                return pCups === itemCups;
              }) || partesList[0] || {};

              const erpRecord = erpList.find(e => {
                const eCups = e?.CUPS || e?.cups || e?.Cups;
                const itemCups = item?.CUPS || item?.cups || cupsValue;
                return eCups === itemCups;
              }) || erpList[0] || {};

              //console.log(`📍 CUPS: ${cupsValue}`);
              //console.log("   Partes:", partesRecord);
              //console.log("   ERP:", erpRecord);

              const meterData = {
                cups: cupsValue,
                partes: partesRecord,
                erp: erpRecord,
                base: item,
                primeLicense: primeRecord,
                validation: validationData,
              };

              //console.log(`✅ Fresh data loaded for ${cupsValue}:`, meterData);
              return meterData;
            } catch (error) {
              console.error(`❌ Error loading data for ${cupsValue}:`, error);
              // Return existing data if API fails
              return selectedMeters.find(m => m.cups === cupsValue) || {
                cups: cupsValue,
                partes: {},
                erp: {},
                base: item,
                primeLicense: null,
                validation: [],
              };
            }
          })
        );

        /*console.log(`✅ All data reloaded from API for tipo ${selectedTipoParteId}`);
        console.log("=".repeat(80));
        console.log("🔍 VERIFICA metersToUse - Dati caricati dalle API");
        console.log("=".repeat(80));
        metersToUse.forEach((meter, idx) => {
          console.log(`\n--- Meter ${idx + 1}: ${meter.cups} ---`);
          console.log("  partes.PCP1:", meter?.partes?.PCP1);
          console.log("  partes.PCP2:", meter?.partes?.PCP2);
          console.log("  partes.PCP3:", meter?.partes?.PCP3);
          console.log("  partes.Tarifa:", meter?.partes?.Tarifa);
          console.log("  partes.PROCESOFACTURACION:", meter?.partes?.PROCESOFACTURACION);
          console.log("  erp.PCP1:", meter?.erp?.PCP1);
          console.log("  erp.PCP2:", meter?.erp?.PCP2);
          console.log("  erp.PCP3:", meter?.erp?.PCP3);
          console.log("  erp.Tarifa:", meter?.erp?.Tarifa);
          console.log("  erp.PROCESOFACTURACION:", meter?.erp?.PROCESOFACTURACION);
        });
        console.log("=".repeat(80));*/

      } else {
        console.log(`ℹ️ Tipo ${selectedTipoParteId}: Using existing meter data (no API reload)`);
      }

      // Build orders with the appropriate data (fresh from API or existing)
      //console.log("\n🔨 Building orders from metersToUse...");
      const ordersToCreate = metersToUse.map((meter, idx) => {
        //console.log(`\n🔨 Building order ${idx + 1} for CUPS: ${meter?.cups}`);
        const order = buildOrderPayload(meter);
        //console.log(`✅ Order ${idx + 1} built with PARAMS:`, order.PARAMS);
        return order;
      });

      const invalidOrders = ordersToCreate
        .map((order, i) => ({ order, index: i }))
        .filter(({ order }) => {
          const errors = validateOrderAgainstSpec(order);
          return errors.length > 0;
        });

      if (invalidOrders.length > 0) {
        const { order, index } = invalidOrders[0];
        const errors = validateOrderAgainstSpec(order);
        const cupsLabel = order.CUPS ? ` (${order.CUPS})` : "";
        toast.error(`Revisa la orden${cupsLabel}: ${errors[0]}`);
        console.warn("❌ Orden inválida según especificaciones", invalidOrders);
        return;
      }

      // 🔥 DETAILED LOGGING: Show payload and API data for each order
      console.log("\n" + "=".repeat(80));
      console.log(`📦 Payload to be sent (POST /orders) - ${ordersToCreate.length} orders`);
      console.log("Tipo de parte:", selectedTipoParteId);
      console.log("Data source:", isSpecialTipo ? "Fresh from API" : "Existing in memory");
      console.log("Number of orders:", ordersToCreate.length);
      console.log("=".repeat(80));

      ordersToCreate.forEach((order, i) => {
        console.log(`\n${"=".repeat(60)}`);
        console.log(`ORDER #${i + 1} - CUPS: ${order.CUPS}`);
        console.log(`${"=".repeat(60)}`);

        console.log("\n📋 COMPLETE ORDER PAYLOAD:");
        console.table(order);

        console.log("\n🔧 PARAMS ARRAY:");
        console.log(order.PARAMS);

        if (isSpecialTipo) {
          const meterForThisOrder = metersToUse[i];
          console.log("\n📡 DATA LOADED FROM API FOR THIS ORDER:");
          console.log("  Partes PCP1:", meterForThisOrder?.partes?.PCP1);
          console.log("  Partes PCP2:", meterForThisOrder?.partes?.PCP2);
          console.log("  Partes PCP3:", meterForThisOrder?.partes?.PCP3);
          console.log("  Partes Tarifa:", meterForThisOrder?.partes?.Tarifa);
          console.log("  ERP PCP1:", meterForThisOrder?.erp?.PCP1);
          console.log("  ERP PCP2:", meterForThisOrder?.erp?.PCP2);
          console.log("  ERP PCP3:", meterForThisOrder?.erp?.PCP3);
          console.log("  ERP Tarifa:", meterForThisOrder?.erp?.Tarifa);
          if (selectedTipoParteId === "6") {
            console.log("  Prime License:", meterForThisOrder?.primeLicense);
          }
        } else {
          console.log("\n💾 Using existing data (no API reload for this tipo)");
        }

        console.log("\n");
      });

      //console.log("📦 Payload che verrebbe inviato:", ordersToCreate);
      const success = await handleSaveOrders(ordersToCreate);
      if (success) {
        setFormData({});
      }

    } finally {
      setSaving(false);
    }
  };

  // Select row event
  const handleToggle = (item) => {
    // Multi-selezione para todos los tipos (including tipo 1 and 6)
    const exists = selectedCups.find((c) => c.CUPS === item.CUPS);
    if (exists) {
      setSelectedCups((prev) => prev.filter((c) => c.CUPS !== item.CUPS));
      setSelectedMeters((prev) => prev.filter((m) => m.cups !== item.CUPS));
    } else {
      setSelectedCups((prev) => [...prev, item]);
    }
  };
  const handleToggleOriginal = (item) => {
    const isSingle = isSingleSelectionTipoParte;

    if (isSingle) {
      // Se è già quella selezionata, deseleziona; altrimenti rimpiazza
      const already = selectedCups.some((c) => c.CUPS === item.CUPS);
      if (already) {
        setSelectedCups([]);
        setSelectedMeters([]);
      } else {
        setSelectedCups([item]);
        // Resetto i meters: verranno ricaricati dall’effetto esistente
        setSelectedMeters([]);
      }
      return;
    }

    // Multi-selezione (altri tipi)
    const exists = selectedCups.find((c) => c.CUPS === item.CUPS);
    if (exists) {
      setSelectedCups((prev) => prev.filter((c) => c.CUPS !== item.CUPS));
      setSelectedMeters((prev) => prev.filter((m) => m.cups !== item.CUPS));
    } else {
      setSelectedCups((prev) => [...prev, item]);
    }
  };

  // 🔹 Quando chiudi la modale
  const handleCloseClientModal = () => {
    setShowClientModal(false);
  };

  // 🔹 Quando confermi la selezione cliente
  const handleSelectClient = () => {
    const client = availableClients.find(
      (c) => c.NOMBRE_CLIENTE === selectedClient
    );
    if (!client) {
      toast.error("Cliente no encontrado");
      return;
    }

    const code = String(client.COD_CLIENTE);
    const clientDisplayName = client.NOMBRE_CLIENTE || "";
    setClientName(clientDisplayName);
    setSelectedClient(clientDisplayName);
    setClientCodId(code); // ✅ questo triggera il nuovo useEffect
    setShowClientModal(false);
    setSelectedCups([]);
    setSelectedMeters([]);
    setFilterCups("");
    setFilterTecnico("");
    setUserFilter("");
    setGroupFilter("");
    setColumnFilters({ ...INITIAL_COLUMN_FILTERS });
    setVisibleCount(INITIAL_BATCH_SIZE);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(CLIENT_CODE_STORAGE_KEY, code);
      window.localStorage.setItem(CLIENT_NAME_STORAGE_KEY, clientDisplayName);
    }
  };

  //#region Filter & Scroll
  // 🔸 1. Applica i filtri colonna per colonna
  const applyColumnFilters = useMemo(() => {
    if (!filteredCups?.length) return [];

    return filteredCups.filter((item) => {
      const match = (value, filter) =>
        !filter ||
        String(value || "")
          .toLowerCase()
          .includes(filter.toLowerCase());

      return (
        match(item.CUPS, columnFilters.cups) &&
        match(item.CntId, columnFilters.cntId) &&
        match(item.NOMBRECT, columnFilters.nombre) &&
        match(item.DIRECCIONSUMINISTRO, columnFilters.direccion) &&
        match(item.Te, columnFilters.Te) &&
        match(item.TIPO_INSTALACION, columnFilters.tipoInst) &&
        match(item.COORDENADA_X, columnFilters.coordX) &&
        match(item.COORDENADA_Y, columnFilters.coordY) &&
        match(item.NS, columnFilters.ns)
      );
    });
  }, [filteredCups, columnFilters]);

  // 🔸 2. Ordinamento dinamico
  const sortedCups = useMemo(() => {
    if (!sortConfig.key) return applyColumnFilters;

    const sorted = [...applyColumnFilters].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? "";
      const bVal = b[sortConfig.key] ?? "";

      const aStr = String(aVal);
      const bStr = String(bVal);

      return sortConfig.direction === "asc"
        ? aStr.localeCompare(bStr, "es", { numeric: true, sensitivity: "base" })
        : bStr.localeCompare(aStr, "es", {
          numeric: true,
          sensitivity: "base",
        });
    });

    return sorted;
  }, [applyColumnFilters, sortConfig]);

  // 🔸 3. Gestione ordinamento
  const handleSort = (columnKey) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        return {
          key: columnKey,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: columnKey, direction: "asc" };
    });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  function buildFormDataForSelection(schema, meter, tipoParteId) {
    /*console.log("🔧 buildFormDataForSelection called:", {
      tipoParteId,
      hasSchema: !!schema?.properties,
      hasAllOf: !!schema?.allOf,
      meterData: meter,
    });*/

    if (!schema?.properties) return {};

    // 🔹 Se lo schema ha allOf, trova il blocco specifico per questo TipoParte
    let effectiveSchema = schema;
    if (schema.allOf && Array.isArray(schema.allOf)) {
      const tipoParteNumeric = Number(tipoParteId);
      //console.log("🔍 Schema has allOf, looking for TipoParte:", tipoParteNumeric  );

      const matchingBlock = schema.allOf.find(
        (block) => block.if?.properties?.TipoParte?.const === tipoParteNumeric
      );

      if (matchingBlock?.then?.properties) {
        //console.log(          "✅ Found matching allOf block with properties:",          Object.keys(matchingBlock.then.properties)        );

        effectiveSchema = {
          ...schema,
          properties: {
            ...schema.properties,
            ...matchingBlock.then.properties,
          },
        };

        //console.log(          "✅ Merged schema properties:",          Object.keys(effectiveSchema.properties)        );
      }
    }

    // 🔹 NUOVO: Controlla se i campi sono dentro Params.properties
    let paramsSchema = null;
    if (effectiveSchema.properties.Params?.properties) {
      paramsSchema = effectiveSchema.properties.Params;
      //console.log("✅ Found nested Params.properties with keys:", Object.keys(paramsSchema.properties) );
    }

    const baseDefaults = computeDefaultFormData(effectiveSchema, meter);
    let next = { ...baseDefaults };

    //console.log("📋 Base defaults:", baseDefaults);

    // Tipo 1: mappature avanzate
    if (tipoParteId === "1") {
      //console.log("🔍 TipoParte 1 - Starting field resolution");
      const dataSources = [
        meter?.erp,
        meter?.partes,
        meter?.base,
        meter?.validation,
      ];

      const index = buildNormalizedDataIndex(...dataSources);

      // 🔹 Se c'è Params.properties, popola i campi dentro Params
      if (paramsSchema?.properties) {
        const paramsPropertyKeys = Object.keys(paramsSchema.properties);
        const paramsData = {};

        //console.log(`\n🔍 Processing ${paramsPropertyKeys.length} Params fields...`);

        paramsPropertyKeys.forEach((key) => {
          const propertySchema = paramsSchema.properties[key];

          // 1) Prova valore diretto nelle sorgenti
          const direct = dataSources.find(
            (src) => src && !isEmptyValue(src[key])
          );

          if (direct && !isEmptyValue(direct[key])) {
            //console.log(`  ✅ "${key}":`, direct[key]);
            paramsData[key] = direct[key];
            return;
          }

          // 2) Prova risoluzione intelligente tramite alias
          const mapped = resolveType1FormFieldValue(key, index);
          if (!isEmptyValue(mapped)) {
            //console.log(`  ✅ "${key}" (mapped):`, mapped);
            paramsData[key] = mapped;
            return;
          }

          // 3) Fallback sicuro
          const fallback = getSafeFallbackValue(key, propertySchema);
          //console.log(`  ⚠️ "${key}" (fallback):`, fallback);
          paramsData[key] = fallback;
        });

        next.Params = paramsData;
        //console.log("✅ Populated Params with", Object.keys(paramsData).length, "fields"  );
      }

      //console.log("✅ Final form data for TipoParte 1:", next);
    }

    // Tipo 6: campi PRIME
    if (tipoParteId === "6") {
      console.log("🔍 TipoParte 6 - Processing PRIME fields");

      // 🔹 Se c'è Params.properties, lavora dentro Params
      const targetSchema = paramsSchema || effectiveSchema;
      const schemaKeys = Object.keys(targetSchema.properties || {});

      const findSchemaKeyForPrimeField = (targetKey) => {
        const normalizedCandidates = buildPrimeLicenseKeyCandidates(
          targetKey
        ).map((c) => normalizeFieldKey(c));
        const matchedKey = schemaKeys.find((schemaKey) =>
          normalizedCandidates.includes(normalizeFieldKey(schemaKey))
        );
        return matchedKey || targetKey;
      };

      const primeData = paramsSchema ? {} : next;

      TYPE6_PRIME_FIELDS.forEach((primeKey) => {
        const schemaKey = findSchemaKeyForPrimeField(primeKey);
        if (!targetSchema.properties[schemaKey]) return;
        const primeValue = resolveValueFromMeter(meter, primeKey);
        if (!isEmptyValue(primeValue)) {
          console.log(
            `  ✅ PRIME field "${primeKey}" → "${schemaKey}":`,
            primeValue
          );
          primeData[schemaKey] = primeValue;
        }
      });

      if (paramsSchema) {
        next.Params = primeData;
      }
    }
    // Tipo  2, 3, 4
    if (tipoParteId === "2" || tipoParteId === "3" || tipoParteId === "4") {
      console.log(`🔍 TipoParte ${tipoParteId} - Processing DATE fields`);

      const targetSchema = paramsSchema || effectiveSchema;
      const schemaKeys = Object.keys(targetSchema.properties || {});

      const findSchemaKeyForDateField = (targetKey) => {
        // Prova prima il nome esatto
        if (schemaKeys.includes(targetKey)) {
          return targetKey;
        }

        // Poi prova gli alias
        const aliasList = TYPE_234_FIELD_ALIASES[targetKey];
        if (aliasList) {
          for (const alias of aliasList) {
            if (schemaKeys.includes(alias)) {
              return alias;
            }
          }
        }

        return targetKey;
      };

      const dateData = paramsSchema ? {} : next;
      const defaultDates = getDefaultDatesForTipoParte(tipoParteId);

      TYPE_234_DATE_FIELDS.forEach((dateFieldKey) => {
        const schemaKey = findSchemaKeyForDateField(dateFieldKey);
        if (!targetSchema.properties[schemaKey]) {
          console.log(`  ⚠️ Schema key not found for "${dateFieldKey}"`);
          return;
        }

        let dateValue;

        // 1) Prova a prendere dal meter
        const valueFromMeter =
          getType234ValueFromSource(meter?.base || {}, dateFieldKey) ||
          getType234ValueFromSource(meter?.partes || {}, dateFieldKey) ||
          getType234ValueFromSource(meter?.erp || {}, dateFieldKey);

        // 2) Usa default dates se disponibili
        if (dateFieldKey === "Fecha_Desde" && !valueFromMeter) {
          dateValue = defaultDates.fechaInicio;
        } else if (dateFieldKey === "Fecha_Hasta" && !valueFromMeter) {
          dateValue = defaultDates.fechaFin;
        } else if (
          dateFieldKey === "Concentrator_identifier" &&
          !valueFromMeter
        ) {
          dateValue = ""; // Campo opzionale
        } else {
          dateValue = valueFromMeter;
        }

        if (!isEmptyValue(dateValue)) {
          console.log(
            `  ✅ DATE field "${dateFieldKey}" → "${schemaKey}":`,
            dateValue
          );
          dateData[schemaKey] = dateValue;
        } else {
          console.log(`  ⚠️ DATE field "${dateFieldKey}" is empty`);
        }
      });

      if (paramsSchema) {
        next.Params = dateData;
        console.log(
          `✅ Populated Params with date fields for tipo ${tipoParteId}:`,
          dateData
        );
      } else {
        console.log(
          `✅ Populated root with date fields for tipo ${tipoParteId}:`,
          dateData
        );
      }
    }

    // Tipo 2, 3, 4: usa la misma lógica de buildParamsForMeter para rellenar fechas
    if (["2", "3", "4"].includes(String(tipoParteId))) {
      console.log(
        `🧩 buildFormDataForSelection → applying buildParamsForMeter for tipo ${tipoParteId}`
      );

      try {
        // obtenemos las fechas correctas desde la función existente
        const paramsArray = buildParamsForMeter({
          base: meter?.base || {},
          partes: meter?.partes || {},
          erp: meter?.erp || {},
          validation: meter?.validation || {},
          primeLicense: meter?.primeLicense || {},
        });

        const [fechaDesde, fechaHasta, concentrator] = paramsArray;

        // aseguramos que se escriban dentro del objeto correcto (Params o raíz)
        const hasParams =
          effectiveSchema?.properties?.Params?.properties?.Fecha_Desde;

        if (hasParams) {
          next.Params = {
            ...(next.Params || {}),
            Fecha_Desde: fechaDesde || "",
            Fecha_Hasta: fechaHasta || "",
            Concentrator_identifier: concentrator || "",
          };
        } else {
          next.Fecha_Desde = fechaDesde || "";
          next.Fecha_Hasta = fechaHasta || "";
          next.Concentrator_identifier = concentrator || "";
        }

        console.log("✅ buildFormDataForSelection → applied default dates:", {
          fechaDesde,
          fechaHasta,
          concentrator,
          hasParams,
        });
      } catch (err) {
        console.error(
          "⚠️ Error applying buildParamsForMeter inside buildFormDataForSelection:",
          err
        );
      }
    }

    return next;
  }

  // 🔸 4. Virtual scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollContainerRef.current;

    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + BATCH_INCREMENT, sortedCups.length)
          );
        }
      },
      { root: container, threshold: 1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sortedCups.length]);

  // 🔸 5. Applica il numero di righe visibili
  const visibleRows = useMemo(
    () => sortedCups.slice(0, visibleCount),
    [sortedCups, visibleCount]
  );
  //#endregion

  return (
    <Layout>
      <div className="dashboard">
        <div className="ventana ventana--workorders ventana--create-workorder mostrar">
          <span className="ventana_tittle">Creación parte de Trabajo</span>

          <div className="ventana_menu">
            <div className="menu_descripcion">
              <span>
                <b>Cliente:</b> {clientName || "Cargando..."}
              </span>
              <span>
                <b>Usuarios</b>
              </span>
            </div>
            <div className="menu_opciones">
              <span
                className={`ficha ${(availableClients?.length || 0) === 0 ? "disable" : ""
                  }`}
                onClick={() => {
                  if ((availableClients?.length || 0) === 0) return;
                  setShowClientModal(true);
                  setSelectedClient(clientName || selectedClient || "");
                }}
              >
                Cambiar cliente
              </span>
              <span
                className={`ficha${isCreateDisabled ? " disable" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isCreateDisabled)
                    handleCreateOrdersClick();
                }}
              >
                {saving ? "Creando partes..." : "Crear parte"}
              </span>
              {/* LIMPIAR SELECCIÓN */}
              <span
                className={`ficha${selectedCups.length === 0 ? " disable" : ""
                  }`}
                onClick={() => {
                  if (selectedCups.length > 0) {
                    setSelectedCups([]);
                    setSelectedMeters([]);
                  }
                }}
                title={
                  selectedCups.length === 0
                    ? "No hay CUPS seleccionados"
                    : "Limpiar la selección actual"
                }
              >
                Limpiar selección
              </span>

              <span className="ficha" onClick={() => navigate("/work-orders")}>
                <i className="bi bi-arrow-left-circle" aria-hidden="true"></i>
                &nbsp;&nbsp;Volver a partes
              </span>
            </div>
          </div>
          <div className="ventana_resultado_visible ventana_resultado_visible--create-workorder">
            {loading ? (
              <p>Cargando...</p>
            ) : (
              <>
                {/* SEZIONE FORM + ASSEGNAZIONE */}
                <div
                  className="order-section"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Left Column: assegnazione */}
                  <div style={{ flex: 1, minWidth: "400px" }}>
                    <div className="input-group">
                      <label>Asignar a *</label>
                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <label>
                          <input
                            type="radio"
                            value="user"
                            checked={assignmentMode === "user"}
                            onChange={(event) =>
                              handleAssignmentChange(event.target.value)
                            }
                          />
                          Usuario / Técnico
                        </label>
                        <label>
                          <input
                            type="radio"
                            value="group"
                            checked={assignmentMode === "group"}
                            onChange={(event) =>
                              handleAssignmentChange(event.target.value)
                            }
                          />
                          Grupo de usuarios
                        </label>
                      </div>

                      {assignmentMode === "user" && (
                        <>
                          <div
                            className="input-group"
                            style={{ paddingLeft: 0 }}
                          >
                            <label>Filtrar usuarios o técnicos</label>
                            <input
                              type="text"
                              value={userFilter}
                              onChange={(event) =>
                                setUserFilter(event.target.value)
                              }
                              placeholder="Buscar por nombre, usuario o COD_ID"
                            />
                          </div>
                          {filteredUsers.length > 0 ? (
                            <OrderCustomSelect
                              options={filteredUsers.map((user) => ({
                                value: String(
                                  user.COD_ID ??
                                  user.cod_id ??
                                  user.id ??
                                  user.USER ??
                                  ""
                                ),
                                label: `${user.USER || user.NOMBRE || "Usuario"
                                  }`, // ✅ niente ID visibile
                              }))}
                              value={selectedUserId}
                              onChange={(value) => setSelectedUserId(value)}
                              placeholder="-- Seleccionar usuario o técnico --"
                            />
                          ) : (
                            <p style={{ color: "#999" }}>
                              No hay usuarios disponibles para este cliente
                            </p>
                          )}
                        </>
                      )}

                      {assignmentMode === "group" && (
                        <>
                          <div
                            className="input-group"
                            style={{ paddingLeft: 0 }}
                          >
                            <label>Filtrar grupos</label>
                            <input
                              type="text"
                              value={groupFilter}
                              onChange={(event) =>
                                setGroupFilter(event.target.value)
                              }
                              placeholder="Buscar por nombre o COD_ID"
                            />
                          </div>
                          {filteredGroups.length > 0 ? (
                            <OrderCustomSelect
                              options={filteredGroups.map((group) => ({
                                value: String(group.COD_ID),
                                label: `${group.NOMBRE || "Grupo"}`, // ✅ niente ID visibile
                              }))}
                              value={selectedGroupId}
                              onChange={(value) => setSelectedGroupId(value)}
                              placeholder="-- Seleccionar grupo --"
                            />
                          ) : (
                            <p style={{ color: "#999" }}>
                              No hay grupos disponibles para el cliente
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Right Column: tipo parte + form */}
                  <div style={{ flex: 1, minWidth: "400px" }}>
                    <div className="input-group">
                      <label>Tipo de parte *</label>
                      <OrderCustomSelect
                        options={schemas.map((schemaItem) => ({
                          value: schemaItem.id,
                          label: schemaItem.label,
                        }))}
                        value={selectedSchemaId}
                        onChange={(value) => setSelectedSchemaId(value)}
                        placeholder="-- Seleccionar tipo de parte --"
                      />
                      {selectedSchema?.schema?.description && (
                        <small style={{ color: "#666" }}>
                          {selectedSchema.schema.description}
                        </small>
                      )}
                    </div>
                    {selectedSchema && selectedSchemaId !== "1" && selectedSchemaId !== "6" ? (
                      <div className="input-group">
                        <label>Datos necesarios para el parte</label>
                        <JsonSchemaForm
                          schema={selectedSchema.schema}
                          formData={{
                            ...formData,
                            TipoParte: Number(selectedSchemaId),
                            TipoParteDescripcion:
                              selectedSchema.schema.allOf?.find(
                                (block) =>
                                  block.if?.properties?.TipoParte?.const ===
                                  Number(selectedSchemaId)
                              )?.then?.properties?.TipoParteDescripcion
                                ?.const ||
                              selectedSchema.label ||
                              "",
                          }}
                          onChange={(data) => setFormData(data.formData)}
                        />
                      </div>
                    ) : selectedSchema && (selectedSchemaId === "1" || selectedSchemaId === "6") ? (
                      <div className="input-group">
                        <p style={{ color: "#666", fontStyle: "italic" }}>
                          Para Tipo Parte {selectedSchemaId}, no es necesario completar datos adicionales. Selecciona los CUPS y asigna el parte.
                        </p>
                      </div>
                    ) : (
                      <div className="input-group">
                        <p style={{ color: "#666" }}>
                          Selecciona el tipo de parte para cargar el formulario.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* TABELLA CON FILTRI, ORDINAMENTO E VIRTUAL SCROLL */}
                <div
                  className="tabla tabla--workorders"
                  ref={scrollContainerRef}
                  style={{
                    overflowY: "auto",
                    overflowX: "auto",
                    whiteSpace: "nowrap", // ✅ evita que las celdas se rompan en líneas
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {loadingMetersList && (
                    <div className="mensaje-carga">
                      Cargando contadores disponibles...
                    </div>
                  )}
                  <table className="workorders-table">
                    <thead>
                      {/* FILTROS POR COLUMNA */}
                      <tr className="filters-row">

                        <th style={columnStyleMap.checkBox}>
                          <input
                            title={
                              selectedCups.length > 0 &&
                                selectedCups.length === visibleRows.length
                                ? "Deseleccionar todos"
                                : "Seleccionar todos"
                            }
                            style={{ marginLeft: "-5px" }}
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Selecciona todos los visibles
                                setSelectedCups(visibleRows);
                              } else {
                                // Deselecciona todos
                                setSelectedCups([]);
                                setSelectedMeters([]);
                              }
                            }}
                            checked={
                              selectedCups.length > 0 &&
                              selectedCups.length === visibleRows.length
                            }
                            ref={(el) => {
                              if (el) {
                                el.indeterminate =
                                  selectedCups.length > 0 &&
                                  selectedCups.length < visibleRows.length;
                              }
                            }}
                          />
                        </th>

                        <th style={columnStyleMap.cups}>
                          <input
                            type="text"
                            placeholder="CUPS"
                            value={columnFilters.cups}
                            onChange={(e) =>
                              setColumnFilters({
                                ...columnFilters,
                                cups: e.target.value,
                              })
                            }
                          />
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "cups")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                        <th style={columnStyleMap.cntId}>
                          <input
                            type="text"
                            placeholder="CntId"
                            value={columnFilters.cntId}
                            onChange={(e) =>
                              setColumnFilters({
                                ...columnFilters,
                                cntId: e.target.value,
                              })
                            }
                          />
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "cntId")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                        <th style={columnStyleMap.nombre}>
                          <input
                            type="text"
                            placeholder="Nombre CT"
                            value={columnFilters.nombre}
                            onChange={(e) =>
                              setColumnFilters({
                                ...columnFilters,
                                nombre: e.target.value,
                              })
                            }
                          />
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "nombre")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                        <th style={columnStyleMap.direccion}>
                          <input
                            type="text"
                            placeholder="Dirección suministro"
                            value={columnFilters.direccion}
                            onChange={(e) =>
                              setColumnFilters({
                                ...columnFilters,
                                direccion: e.target.value,
                              })
                            }
                          />
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "direccion")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                        <th style={columnStyleMap.tipoE}>
                          <input
                            type="text"
                            placeholder="Tipo E."
                            value={columnFilters.Te}
                            onChange={(e) =>
                              setColumnFilters({
                                ...columnFilters,
                                Te: e.target.value,
                              })
                            }
                          />
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "tipoE")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                        <th style={columnStyleMap.tipoInst}>
                          <input
                            type="text"
                            placeholder="Tipo Inst."
                            value={columnFilters.tipoInst}
                            onChange={(e) =>
                              setColumnFilters({
                                ...columnFilters,
                                tipoInst: e.target.value,
                              })
                            }
                          />
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "tipoInst")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                        <th style={columnStyleMap.coordX}>
                          <input
                            type="text"
                            placeholder="Coord. X"
                            value={columnFilters.coordX}
                            onChange={(e) =>
                              setColumnFilters({
                                ...columnFilters,
                                coordX: e.target.value,
                              })
                            }
                          />
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "coordX")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                        <th style={columnStyleMap.coordY}>
                          <input
                            type="text"
                            placeholder="Coord. Y"
                            value={columnFilters.coordY}
                            onChange={(e) =>
                              setColumnFilters({
                                ...columnFilters,
                                coordY: e.target.value,
                              })
                            }
                          />
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "coordY")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                        <th style={columnStyleMap.ns}>
                          <input
                            type="text"
                            placeholder="NS"
                            value={columnFilters.ns}
                            onChange={(e) =>
                              setColumnFilters({
                                ...columnFilters,
                                ns: e.target.value,
                              })
                            }
                          />
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "ns")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                        <th
                          style={{
                            ...columnStyleMap.acciones,
                            textAlign: "right",
                          }}
                        >
                          <button
                            onClick={() =>
                              setColumnFilters({
                                cups: "",
                                cntId: "",
                                nombre: "",
                                direccion: "",
                                tipoInst: "",
                                Te: "",
                                coordX: "",
                                coordY: "",
                                ns: "",
                              })
                            }
                            className="btn-limpiar-filtros"
                          >
                            Limpiar
                          </button>
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "acciones")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                      </tr>

                      {/* CABECERAS CON ORDEN Y REDIMENSION */}
                      <tr>
                        <th></th>

                        <th
                          style={columnStyleMap.cups}
                          onClick={() => handleSort("CUPS")}
                          aria-sort={
                            sortConfig.key === "CUPS"
                              ? sortConfig.direction
                              : "none"
                          }
                        >
                          CUPS {renderSortIndicator("CUPS")}
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "cups")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>

                        <th
                          style={columnStyleMap.cntId}
                          onClick={() => handleSort("CntId")}
                          aria-sort={
                            sortConfig.key === "CntId"
                              ? sortConfig.direction
                              : "none"
                          }
                        >
                          CntID {renderSortIndicator("CntId")}
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "cntId")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>

                        <th
                          style={columnStyleMap.nombre}
                          onClick={() => handleSort("NOMBRECT")}
                          aria-sort={
                            sortConfig.key === "NOMBRECT"
                              ? sortConfig.direction
                              : "none"
                          }
                        >
                          Nombre CT {renderSortIndicator("NOMBRECT")}
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "nombre")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>

                        <th
                          style={columnStyleMap.direccion}
                          onClick={() => handleSort("DIRECCIONSUMINISTRO")}
                          aria-sort={
                            sortConfig.key === "DIRECCIONSUMINISTRO"
                              ? sortConfig.direction
                              : "none"
                          }
                        >
                          Dirección suministro{" "}
                          {renderSortIndicator("DIRECCIONSUMINISTRO")}
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "direccion")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>

                        <th
                          style={columnStyleMap.tipoE}
                          onClick={() => handleSort("Te")}
                          aria-sort={
                            sortConfig.key === "Te"
                              ? sortConfig.direction
                              : "none"
                          }
                        >
                          Tipo E. {renderSortIndicator("Te")}
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "tipoE")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>

                        <th
                          style={columnStyleMap.tipoInst}
                          onClick={() => handleSort("TIPO_INSTALACION")}
                          aria-sort={
                            sortConfig.key === "TIPO_INSTALACION"
                              ? sortConfig.direction
                              : "none"
                          }
                        >
                          Tipo Inst. {renderSortIndicator("TIPO_INSTALACION")}
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "tipoInst")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>

                        <th
                          style={columnStyleMap.coordX}
                          onClick={() => handleSort("COORDENADA_X")}
                          aria-sort={
                            sortConfig.key === "COORDENADA_X"
                              ? sortConfig.direction
                              : "none"
                          }
                        >
                          Coord. X {renderSortIndicator("COORDENADA_X")}
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "coordX")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>

                        <th
                          style={columnStyleMap.coordY}
                          onClick={() => handleSort("COORDENADA_Y")}
                          aria-sort={
                            sortConfig.key === "COORDENADA_Y"
                              ? sortConfig.direction
                              : "none"
                          }
                        >
                          Coord. Y {renderSortIndicator("COORDENADA_Y")}
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "coordY")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>

                        <th
                          style={columnStyleMap.ns}
                          onClick={() => handleSort("NS")}
                          aria-sort={
                            sortConfig.key === "NS"
                              ? sortConfig.direction
                              : "none"
                          }
                        >
                          NS {renderSortIndicator("NS")}
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "ns")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>

                        <th style={columnStyleMap.acciones}>
                          -
                          <div
                            className="column-resizer"
                            onMouseDown={(event) =>
                              handleColumnResizeMouseDown(event, "acciones")
                            }
                            onClick={handleResizeHandleClick}
                          />
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {visibleRows.length === 0 ? (
                        <tr>
                          <td colSpan={11} style={{ textAlign: "center" }}>
                            No se encontraron resultados.
                          </td>
                        </tr>
                      ) : (
                        visibleRows.map((item) => {
                          const isItemSelected = selectedCups.some(
                            (c) => c.CUPS === item.CUPS
                          );
                          const disableCheckbox = false;

                          return (
                            <tr
                              key={item.CUPS}
                              onClick={() => handleToggle(item)}
                              style={{ cursor: "pointer" }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={isItemSelected}
                                  disabled={disableCheckbox}
                                  onChange={() => handleToggle(item)}
                                  onClick={(e) => e.stopPropagation()}  // ✅ PREVENTS EVENT BUBBLING
                                />
                              </td>

                              <td style={columnStyleMap.cups} title={item.CUPS}>
                                {item.CUPS}
                              </td>

                              <td
                                style={columnStyleMap.cntId}
                                title={item.CntId}
                              >
                                {item.CntId}
                              </td>

                              <td style={columnStyleMap.nombre}>
                                {item.NOMBRECT}
                              </td>

                              <td style={columnStyleMap.direccion}>
                                {item.DIRECCIONSUMINISTRO}
                              </td>

                              <td style={columnStyleMap.tipoE}>{item.Te}</td>

                              <td style={columnStyleMap.tipoInst}>
                                {item.TIPO_INSTALACION}
                              </td>

                              <td
                                style={columnStyleMap.coordX}
                                title={item.COORDENADA_X ?? ""}
                              >
                                {item.COORDENADA_X}
                              </td>

                              <td
                                style={columnStyleMap.coordY}
                                title={item.COORDENADA_Y ?? ""}
                              >
                                {item.COORDENADA_Y}
                              </td>

                              <td style={columnStyleMap.ns}>{item.NS}</td>

                              <td style={columnStyleMap.acciones}></td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>

                  {/* SENTINEL per virtual scroll */}
                  <div
                    ref={sentinelRef}
                    style={{ height: "1px", width: "100%" }}
                  ></div>
                  {visibleRows.length < sortedCups.length && (
                    <div className="mensaje-carga">
                      Cargando más resultados…
                    </div>
                  )}
                </div>

                {loadingMeters && (
                  <p style={{ marginTop: "1rem", fontWeight: 600 }}>
                    Cargando información adicional de los CUPS seleccionados...
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {showClientModal && (
        <ModalSelectClient
          clients={availableClients}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          onSelect={handleSelectClient}
          onClose={handleCloseClientModal}
        />
      )}
    </Layout>
  );
}

export default CreateWorkOrder;
